/**
 * Factory for WebSked Tasks
 * @author Vinny DeFrancesco, Brendan Magee
 */

function TaskFactory(
  $http,
  $q,
  $log,
  $translate,
  collectionsService,
  schemaService,
  permissionsService,
  Story
) {
  function Task(taskData = {}) {
    const data = taskData;
    if (!(this instanceof Task)) {
      return new Task(data);
    }

    const events = data.events;

    if (events && events.length) {
      const originalEvent = events[0];
      const originalUser = originalEvent.user;

      this.originalNote = originalEvent.note;

      if (originalUser.username === 'SYSTEM') {
        originalUser.name = $translate.instant('SYSTEM').toUpperCase();
      }
    }

    Task.getTaskTypeLabel(data).then((typeLabel) => {
      this.typeLabel = typeLabel;
    });

    schemaService.getTaskInfo('types').then((taskTypes) => {
      const currTaskType = taskTypes.find(
        taskType => taskType.id === data.type
      );

      if (currTaskType) {
        this.customFieldLabels = currTaskType.customFields;
      }
    });

    if (data.latestEvent) {
      Task.getTaskStatusLabel(data).then((statusLabel) => {
        this.statusLabel = statusLabel;
      });
    }

    if (data.story) {
      data.story = new Story(data.story);
    }

    const lastEventWithNote =
      events && events.reverse().find(evt => evt.note && evt.note !== '');
    this.latestNote = lastEventWithNote ? lastEventWithNote.note : '';

    angular.extend(this, data);
  }

  Task.getTaskInfo = (infoCategory, id) =>
    schemaService.getTaskInfo(infoCategory).then((results) => {
      const matchingResult = results.find(res => `${id}` === `${res.id}`);

      return matchingResult
        ? matchingResult.name
        : $translate.instant('UNKNOWN');
    });

  Task.getTaskTypeLabel = data => Task.getTaskInfo('types', data.type);

  Task.getTaskStatusLabel = data =>
    Task.getTaskInfo('statuses', data.latestEvent.status);

  Task.get = (taskId) => {
    const deferred = $q.defer();
    const endpoint = `api/tasks/${taskId}`;
    const config = {
      params: { includeStory: true }
    };

    $http.get(endpoint, config).then(
      (response) => {
        deferred.resolve(new Task(response.data));
      },
      (error) => {
        $log.error(error);

        deferred.reject(error);
      }
    );

    return deferred.promise;
  };

  Task.query = async (params) => {
    try {
      const { data } = await $http.get('api/tasks', {
        params: { size: 20, ...params }
      });

      if (data && Array.isArray(data.results)) {
        const tasks = await collectionsService.addUsagesToStories(
          data.results,
          'task'
        );

        return { ...data, results: tasks.map(task => new Task(task)) };
      }

      return { ...data, results: data.results.map(task => new Task(task)) };
    } catch (error) {
      return error;
    }
  };

  Task.prototype.save = function saveTask(data, storyID = null) {
    const deferred = $q.defer();
    const payload = angular.extend({}, this, data);
    const endpoint = 'api/tasks';

    payload.storyID = storyID;

    if (!data.type && data.type !== 0) {
      throw new Error('Task type is required');
    }

    if (data.deadline) {
      payload.deadline = Math.floor(data.deadline / 1000);
    }

    if (data.startDate) {
      payload.startDate = Math.floor(data.startDate / 1000);
    }

    $http.post(endpoint, payload).then(
      (response) => {
        deferred.resolve(new Task(response.data));
      },
      (error) => {
        $log.error(error);

        deferred.reject(error);
      }
    );

    return deferred.promise;
  };

  Task.prototype.update = function updateTask(data) {
    const deferred = $q.defer();
    const endpoint = `api/tasks/${this.id}/events`;

    const payload = {
      assignedGroup: data.assignedGroup,
      assignedUser: data.assignedUser,
      customFields: data.customFields,
      status: data.status,
      priority: data.priority,
      deadline: data.deadline,
      note: data.note,
      website: data.website,
      startDate: data.startDate
    };

    if (data.status === 4) {
      // This task has been rejected (4) by the assigned user
      payload.assignedUser = null;
    }

    if (data.deadline && data.deadline !== this.latestEvent.deadline) {
      payload.deadline = Math.floor(data.deadline / 1000);
    }

    if (data.startDate && data.startDate !== this.latestEvent.startDate) {
      payload.startDate = Math.floor(data.startDate / 1000);
    }

    $http.post(endpoint, payload).then(
      (response) => {
        deferred.resolve(new Task(response.data));
      },
      (error) => {
        $log.error(error);

        deferred.reject(error);
      }
    );

    return deferred.promise;
  };

  Task.prototype.delete = function deleteTask() {
    const deferred = $q.defer();
    const endpoint = `api/tasks/${this.id}`;

    $http.delete(endpoint).then(
      (response) => {
        deferred.resolve(response);
      },
      (error) => {
        $log.error(error);

        deferred.reject(error);
      }
    );

    return deferred.promise;
  };

  Task.prototype.getLatestEventProp = function getProp(prop = null) {
    if (this.latestEvent && prop) {
      let value = this.latestEvent[prop];
      value =
        prop === 'deadline' || prop === 'startDate' ? value * 1000 : value;
      return value;
    }

    return null;
  };

  Task.prototype.isDeleteEnabled = function checkDeleteEnabled() {
    return permissionsService.isAuthorized('TD', this.website);
  };

  return Task;
}

const arr = [
  '$http',
  '$q',
  '$log',
  '$translate',
  'collectionsService',
  'schemaService',
  'permissionsService',
  'Story',
  TaskFactory
];

export default function (ngModule) {
  ngModule.factory('Task', arr);
}
